/**
 * The invite sheet's always-visible top: the role-correct sharing header.
 *
 * `inviteHeaderModel.ts` is the single authority for which mechanism a
 * viewer sees. `/join-grant/get` answers an ordinary member with
 * `FORBIDDEN_ROLE`, so the wrong query is switched off via `enabled` rather
 * than skipped with a conditional hook call.
 */
import { useJoinGrantGet, useShareLinkGet } from "@sportsmet/pool-sdk/private";
import { Button, CopyField, InlineBanner, Skeleton } from "@sportsmet/ui";
import type { MouseEvent, ReactNode } from "react";

import { extractErrorCode } from "../../errors/apiErrorBus";
import { resolveInviteHeaderModel } from "./inviteHeaderModel";
import { PoolQrButton } from "./PoolQrButton";
import {
  buildInviteMailtoHref,
  buildInviteShareBody,
  buildInviteShareSubject,
} from "./inviteShareHref";
import {
  COPY_INVITE_CODE_LABEL,
  COPY_INVITE_LINK_LABEL,
  INVITE_CODE_FIELD_LABEL,
  INVITE_HEADER_FAILED_MESSAGE,
  INVITE_HEADER_LOADING_LABEL,
  INVITE_HEADER_RETRY_LABEL,
  INVITE_LINK_FIELD_LABEL,
  INVITE_SHARING_OFF_MESSAGE,
  SHARE_LABEL,
} from "./inviteSheetCopy";

export type typeInviteShareHeaderProps = {
  poolId: string;
  poolName: string;
  viewerPoolRole: "owner" | "admin" | "member";
};

export const InviteShareHeader = ({
  poolId,
  poolName,
  viewerPoolRole,
}: typeInviteShareHeaderProps): ReactNode => {
  const isCommissioner =
    viewerPoolRole === "owner" || viewerPoolRole === "admin";

  // Both hooks are called unconditionally, with the wrong one switched off
  // through `enabled`. Never call a hook inside an `if`, and never call only
  // one and pick which with a ternary: both crash on the first role change.
  const grant = useJoinGrantGet({ poolId }, { enabled: isCommissioner });
  const shareLink = useShareLinkGet({ poolId }, { enabled: !isCommissioner });

  const model = resolveInviteHeaderModel({
    viewerPoolRole,
    grant: {
      isLoading: grant.isLoading,
      isTransportError: grant.isError,
      errorCode: extractErrorCode(grant.data),
      joinCode: grant.data?.data?.joinCode ?? null,
      joinUrl: grant.data?.data?.joinUrl ?? null,
    },
    shareLink: {
      isLoading: shareLink.isLoading,
      isTransportError: shareLink.isError,
      errorCode: extractErrorCode(shareLink.data),
      shareUrl: shareLink.data?.data?.shareUrl ?? null,
      active: shareLink.data?.data?.active ?? false,
    },
  });

  function refetchForRole(): void {
    if (isCommissioner) {
      void grant.refetch();
    } else {
      void shareLink.refetch();
    }
  }

  function handleShareClick(
    event: MouseEvent<HTMLAnchorElement>,
    inviteUrl: string,
  ): void {
    if (
      typeof navigator === "undefined" ||
      typeof navigator.share !== "function"
    ) {
      return;
    }
    event.preventDefault();
    const subject = buildInviteShareSubject(poolName);
    const body = buildInviteShareBody({ poolName, inviteUrl });
    void navigator
      .share({ title: subject, text: body, url: inviteUrl })
      .catch(() => {
        // A fan dismissing the OS share sheet rejects this promise. That is
        // not an error and never raises the error modal or a toast.
      });
  }

  function renderShareControl(inviteUrl: string): ReactNode {
    const mailtoHref = buildInviteMailtoHref({ poolName, inviteUrl });
    return (
      <div className="flex flex-wrap gap-2">
        <Button asChild variant="secondary" size="sm">
          <a
            href={mailtoHref}
            data-testid="invite-header-share"
            onClick={(event) => {
              handleShareClick(event, inviteUrl);
            }}
          >
            {SHARE_LABEL}
          </a>
        </Button>
        {/* ISSUE-1339. The same link as a QR code, for a friend in the room. */}
        <PoolQrButton
          inviteUrl={inviteUrl}
          poolName={poolName}
          data-testid="invite-header-qr"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="invite-share-header">
      {model.phase === "loading" ? (
        <div data-testid="invite-header-loading">
          <Skeleton shape="control" label={INVITE_HEADER_LOADING_LABEL} />
        </div>
      ) : null}
      {model.phase === "failed" ? (
        <div data-testid="invite-header-failed">
          <InlineBanner
            variant="warning"
            message={INVITE_HEADER_FAILED_MESSAGE}
            action={{
              label: INVITE_HEADER_RETRY_LABEL,
              onAction: () => {
                refetchForRole();
              },
            }}
          />
        </div>
      ) : null}
      {model.phase === "sharingOff" ? (
        <p
          className="text-body text-ink-secondary"
          data-testid="invite-header-sharing-off"
        >
          {INVITE_SHARING_OFF_MESSAGE}
        </p>
      ) : null}
      {model.phase === "commissioner" ? (
        <>
          <CopyField
            label={INVITE_CODE_FIELD_LABEL}
            value={model.inviteCode}
            copyLabel={COPY_INVITE_CODE_LABEL}
            data-testid="invite-header-code"
          />
          <CopyField
            label={INVITE_LINK_FIELD_LABEL}
            value={model.inviteUrl}
            copyLabel={COPY_INVITE_LINK_LABEL}
            data-testid="invite-header-link"
          />
          {renderShareControl(model.inviteUrl)}
        </>
      ) : null}
      {model.phase === "member" ? (
        <>
          <CopyField
            label={INVITE_LINK_FIELD_LABEL}
            value={model.inviteUrl}
            copyLabel={COPY_INVITE_LINK_LABEL}
            data-testid="invite-header-link"
          />
          {renderShareControl(model.inviteUrl)}
        </>
      ) : null}
    </div>
  );
};
